import { IApplicationData } from "../../model/application-data";
import { IAuthenticationData } from "../../model/authentication-data";
import { IAuthenticationResponse } from "../../model/security/response/authentication-response";
import { ISessionTokensResponse } from "../../model/security/response/session-tokens-response";
import LocalEnvironment from "./local-environment";
import { getProductName } from "./govhub-brand";
import { resolveModuleKey } from "./module-hostname";
import { ModuleId } from "../../model/module-functionalities";
import { ADM_MODULE_ID } from "../../model/adm/adm-enums";

// Módulo e URLs das APIs a partir do hostname (fora de localhost)
export const buildApplicationData = (hostname: string): IApplicationData => {

    const applicationData = {} as IApplicationData;

    const url = hostname.toLowerCase();

    const moduleKey = resolveModuleKey(url);

    let moduleApi: string = moduleKey;

    let autenticacaoApi = "autenticacao";

    if (url.includes("sand")) {
        moduleApi += "-sand";
        autenticacaoApi += "-sand";
    }

    if (moduleKey === "sdp") {
        applicationData.moduleId = ModuleId.Sdp
        applicationData.moduleName = "SDP"
        applicationData.initialPage = "dashboard";
        applicationData.moduleDescription = "Diagramação e Publicação";
    } else if (moduleKey === "scp") {
        applicationData.moduleId = ModuleId.Scp
        applicationData.moduleName = "SCP"
        applicationData.initialPage = "dashboard";
        applicationData.moduleDescription = "Contratações Públicas";
    } else if (moduleKey === "sge") {
        applicationData.moduleId = ModuleId.Sge
        applicationData.moduleName = "SGE"
        applicationData.initialPage = "dashboard";
        applicationData.moduleDescription = "Emendas Parlamentares";
    } else if (moduleKey === "apm") {
        applicationData.moduleId = ModuleId.Spm
        applicationData.moduleName = "APM"
        applicationData.initialPage = "dashboard";
        applicationData.moduleDescription = "Gestão de Portal da Entidade";
    } else if (moduleKey === "ser") {
        applicationData.moduleId = ModuleId.Ser
        applicationData.moduleName = "SER"
        applicationData.initialPage = "dashboard";
        applicationData.moduleDescription = "Envio de Remessas";
    } else if (moduleKey === "ouv") {
        applicationData.moduleId = ModuleId.Ouv
        applicationData.moduleName = "OUV"
        applicationData.initialPage = "dashboard";
        applicationData.moduleDescription = "Ouvidoria";
    } else if (moduleKey === "sic") {
        applicationData.moduleId = ModuleId.Sic
        applicationData.moduleName = "SIC"
        applicationData.initialPage = "dashboard";
        applicationData.moduleDescription = "Serviço de Informação ao Cidadão";
    } else if (moduleKey === "ged") {
        applicationData.moduleId = ModuleId.Ged
        applicationData.moduleName = "GED"
        applicationData.initialPage = "dashboard";
        applicationData.moduleDescription = "Gestão Eletrônica de Documentos";
    } else if (moduleKey === "adm") {
        applicationData.moduleId = ADM_MODULE_ID
        applicationData.moduleName = "ADM"
        applicationData.initialPage = "dashboard";
        applicationData.moduleDescription = "Administração da Empresa";
    }

    if (url.includes("ipmbrasil")) {
        applicationData.multitenant = 7
        applicationData.baseUrlAuthentication = "http://account.api.ipmbrasil.org.br/";
        applicationData.baseUrl = moduleApi === "apm" || moduleApi === "ser" || moduleApi === "ouv" || moduleApi === "sic" || moduleApi === "ged" || moduleApi === "adm" ? `http://${moduleApi}.api.ipmbrasil.org.br/` : "http://scp.api.ipmbrasil.org.br/";
    } else if (url.includes("docgedsistemas")) {
        applicationData.multitenant = 3851
        applicationData.baseUrlAuthentication = `https://api.${autenticacaoApi}.docgedsistemas.com.br/`;
        applicationData.baseUrl = `https://api.${moduleApi}.docgedsistemas.com.br/`;
    } else if (url.includes("airdoc")) {
        applicationData.multitenant = 3901
        applicationData.baseUrlAuthentication = `https://api.${autenticacaoApi}.airdoc.com.br/`;
        applicationData.baseUrl = `https://api.${moduleApi}.airdoc.com.br/`;
    }

    return applicationData;
};

const resolveApplicationData = (): IApplicationData => {

    const url = window.location.hostname.toLowerCase();

    const applicationData = LocalEnvironment.isLocalhost() ? LocalEnvironment.getApplicationData() : buildApplicationData(url);

    applicationData.productName = getProductName(applicationData.moduleId);
    applicationData.isSandbox = url.includes("sand");

    return applicationData;
};

// O hostname não muda na página e a troca de ambiente local recarrega a página (window.location.assign),
// então calcula uma vez só. Em localhost sem módulo escolhido não guarda: a escolha ainda vai acontecer.
let cachedApplicationData: IApplicationData | undefined;

const getApplicationData = (): IApplicationData => {
    if (cachedApplicationData) return cachedApplicationData;
    const applicationData = resolveApplicationData();
    if (applicationData.moduleId !== undefined) cachedApplicationData = applicationData;
    return applicationData;
};

const getAuthenticationData = (): IAuthenticationData | undefined => {
    const value = localStorage.getItem("session_application");
    if (!value) return undefined;
    try {
        return JSON.parse(value);
    } catch {
        // Sessão corrompida no navegador: descarta para cair no login em vez de quebrar a tela
        localStorage.removeItem("session_application");
        return undefined;
    }
};

const setAuthenticationData = (data: IAuthenticationResponse) => {
    const application = Object.assign({} as IAuthenticationData, data)
    localStorage.setItem("session_application", JSON.stringify(application));
};

const clearAuthenticationData = () => {
    localStorage.removeItem("session_application");
};

// Nome e e-mail alterados pelo perfil: a sessão (lida pelas telas) acompanha; o token só muda no próximo login
const updateSessionUser = (patch: { name?: string; email?: string }) => {
    const current = getAuthenticationData();
    if (!current?.user) return;
    const user = { ...current.user };
    if (patch.name) user.name = patch.name;
    if (patch.email) user.email = patch.email;
    localStorage.setItem("session_application", JSON.stringify({ ...current, user }));
};


// Tokens renovados pelo refresh token: usuário e empresa da sessão continuam os mesmos
const updateSessionTokens = (tokens: ISessionTokensResponse) => {
    const current = getAuthenticationData();
    if (!current) return;
    localStorage.setItem("session_application", JSON.stringify({
        ...current,
        bearerToken: tokens.bearerToken,
        expiresAt: tokens.expiresAt,
        refreshToken: tokens.refreshToken,
        refreshTokenExpiresAt: tokens.refreshTokenExpiresAt
    }));
};


const ApplicationAppService = {
    getAuthenticationData, setAuthenticationData, clearAuthenticationData, getApplicationData, updateSessionUser, updateSessionTokens
};

export default ApplicationAppService;
